import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RefreshCw, History, FlaskConical, ListChecks, Info } from "lucide-react";
import { toast } from "sonner";
import { KillSwitchPanel } from "@/components/sistema/KillSwitchPanel";
import { InstallChecklist } from "@/components/sistema/InstallChecklist";

type FlagRow = {
  flag_key: string;
  enabled: boolean;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
};

function mapRpcError(message: string): string {
  if (message.includes("reason_required_min_5_chars")) return "Motivo obrigatório (mínimo 5 caracteres).";
  if (message.includes("flag_not_found")) return "Flag não encontrada em cortex_feature_flags.";
  if (message.includes("authentication_required")) return "Autenticação necessária para alterar flags.";
  return message;
}

export default function SistemaFlags() {
  const { user } = useAuth();
  const role = user?.role;
  const qc = useQueryClient();

  const { data: ctx } = useQuery({
    queryKey: ["current-user-context"],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_current_user_context");
      if (error) throw error;
      return data as { org_id?: string | null };
    },
  });
  const orgId = ctx?.org_id ?? undefined;

  const [editing, setEditing] = useState<{ flag_key: string; current: boolean } | null>(null);
  const [pendingEnabled, setPendingEnabled] = useState<boolean>(false);
  const [reason, setReason] = useState("");

  const { data: flags = [], isLoading } = useQuery({
    queryKey: ["cortex-feature-flags-admin"],
    queryFn: async (): Promise<FlagRow[]> => {
      const { data, error } = await supabase
        .from("cortex_feature_flags")
        .select("flag_key, enabled, description, updated_at, updated_by")
        .order("flag_key", { ascending: true });
      if (error) throw error;
      return (data ?? []) as FlagRow[];
    },
  });

  const { data: rollbackEvents = [] } = useQuery({
    queryKey: ["rollback-events", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from("rollback_events")
        .select("*")
        .eq("org_id", orgId)
        .order("triggered_at", { ascending: false })
        .limit(50);
      return data ?? [];
    },
  });

  const toggleMut = useMutation({
    mutationFn: async () => {
      if (!editing) throw new Error("Nenhuma flag selecionada.");
      const { data, error } = await supabase.rpc("set_cortex_flag", {
        p_flag_key: editing.flag_key,
        p_enabled: pendingEnabled,
        p_reason: reason,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success(`Flag atualizada: ${editing?.flag_key} → ${pendingEnabled ? "on" : "off"}`);
      setEditing(null);
      setReason("");
      qc.invalidateQueries({ queryKey: ["cortex-feature-flags-admin"] });
    },
    onError: (e: Error) => toast.error(mapRpcError(e.message)),
  });

  const runSmokeMut = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("smoke-tests-runner");
      if (error) throw error;
      return data;
    },
    onSuccess: (d: any) => toast.success(`${d.passed}/${d.total} smoke tests verdes`),
    onError: (e: Error) => toast.error(e.message),
  });

  if (role !== "owner" && role !== "admin" && role !== "superadmin") {
    return <Navigate to="/dashboard" replace />;
  }

  const dirty = editing ? pendingEnabled !== editing.current : false;
  const reasonValid = reason.trim().length >= 5;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold font-headline">Sistema · Flags & Rollout</h1>
        <p className="text-sm text-muted-foreground">
          Toggle binário (REGRA 1). Cada alteração exige motivo e é gravada em audit_logs.
        </p>
      </header>

      {orgId && <KillSwitchPanel orgId={orgId} />}

      <Tabs defaultValue="flags" className="space-y-4">
        <TabsList>
          <TabsTrigger value="flags"><FlaskConical className="w-4 h-4 mr-1" /> Flags</TabsTrigger>
          <TabsTrigger value="rollbacks"><History className="w-4 h-4 mr-1" /> Rollbacks</TabsTrigger>
          <TabsTrigger value="smoke"><RefreshCw className="w-4 h-4 mr-1" /> Smoke tests</TabsTrigger>
          <TabsTrigger value="checklist"><ListChecks className="w-4 h-4 mr-1" /> Checklist</TabsTrigger>
        </TabsList>

        <TabsContent value="flags" className="space-y-2">
          {isLoading ? <Skeleton className="h-40" /> : (
            <TooltipProvider delayDuration={150}>
              {flags.map((f) => (
                <Card key={f.flag_key}>
                  <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3 px-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <code className="text-sm font-mono">{f.flag_key}</code>
                        {f.description && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs"><p className="text-xs">{f.description}</p></TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant={f.enabled ? "default" : "outline"}>{f.enabled ? "on" : "off"}</Badge>
                        <span>· atualizado {new Date(f.updated_at).toLocaleString("pt-BR")}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={f.enabled}
                        onCheckedChange={(next) => {
                          setEditing({ flag_key: f.flag_key, current: f.enabled });
                          setPendingEnabled(next);
                          setReason("");
                        }}
                        aria-label={`Toggle ${f.flag_key}`}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TooltipProvider>
          )}
        </TabsContent>

        <TabsContent value="rollbacks">
          <Card>
            <CardHeader><CardTitle>Eventos de rollback automático</CardTitle></CardHeader>
            <CardContent>
              {rollbackEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum rollback automático registrado.</p>
              ) : rollbackEvents.map((r: any) => (
                <div key={r.id} className="border-b py-2 last:border-0 text-sm">
                  <div className="flex justify-between">
                    <code className="font-mono">{r.flag_key}</code>
                    <Badge variant="destructive">{r.trigger}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(r.triggered_at).toLocaleString("pt-BR")}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="smoke">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Smoke tests (Doc 3 §9)
                <Button size="sm" onClick={() => runSmokeMut.mutate()} disabled={runSmokeMut.isPending}>
                  Executar agora
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {runSmokeMut.data ? (
                <div className="space-y-1 text-sm">
                  <p className="font-medium">
                    {(runSmokeMut.data as any).passed}/{(runSmokeMut.data as any).total} testes verdes
                  </p>
                  {(runSmokeMut.data as any).results.map((t: any) => (
                    <div key={t.name} className="flex justify-between border-b py-1 last:border-0">
                      <code className="font-mono text-xs">{t.name}</code>
                      <Badge variant={t.ok ? "default" : "destructive"}>{t.ok ? "ok" : "falhou"}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Clique em "Executar agora" para rodar os testes.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="checklist">
          <InstallChecklist />
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar flag: <code className="text-sm">{editing?.flag_key}</code></DialogTitle>
            <DialogDescription>
              Estado atual: <strong>{editing?.current ? "on" : "off"}</strong> → novo:{" "}
              <strong>{pendingEnabled ? "on" : "off"}</strong>. A ação será gravada em audit_logs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded border px-3 py-2">
              <span className="text-sm">Habilitada</span>
              <Switch
                checked={pendingEnabled}
                onCheckedChange={setPendingEnabled}
                aria-label="Novo estado da flag"
              />
            </div>
            <div>
              <label className="text-xs font-medium">Motivo (mínimo 5 caracteres)</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Ex.: ligando ToM injection após validação de calibração."
              />
              {!reasonValid && reason.length > 0 && (
                <p className="text-xs text-destructive mt-1">Motivo precisa ter pelo menos 5 caracteres.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setReason(""); }}>Cancelar</Button>
            <Button
              onClick={() => toggleMut.mutate()}
              disabled={!dirty || !reasonValid || toggleMut.isPending}
            >
              Confirmar alteração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
